import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../api";
import { toast } from "sonner";
import { ArrowRight, Loader2, Check, Sparkles, Video, Users } from "lucide-react";

const PRODUCTS = [
  {
    key: "jidnyasa",
    title: "Jidnyasa Jr Leaders Program",
    shortTitle: "JJLP",
    subtitle: "Live weekly classes · Self + World",
    hint: "Age 7–13. One live 60-min class every week — Knowledge of Self + Knowledge of World.",
    icon: Video,
    isJidnyasa: true,
    bullets: [
      "Live 60-min weekly class",
      "Knowledge of Self + Knowledge of World",
      "Session recordings for subscribers",
    ],
  },
  {
    key: "neuroeq",
    title: "NeuroEQ Growth Program",
    shortTitle: "NGP",
    subtitle: "Parent-led Sunday ritual",
    hint: "Age 4–13. 30 minutes of guided conversation each Sunday. Children gradually self-coach.",
    icon: Sparkles,
    bullets: [
      "Personalized story every Sunday",
      "Guided parent-child questions",
      "Children gradually self-coach using the material",
    ],
  },
  {
    key: "both",
    title: "Both programs (Joint)",
    shortTitle: "JJLP + NGP",
    subtitle: "Best value",
    hint: "One subscription. JJLP live classes + NGP Sunday ritual — for the whole family.",
    icon: Users,
    highlight: true,
    bullets: [
      "Everything in JJLP",
      "Everything in NGP",
      "One subscription, one bill",
    ],
  },
];

export default function Paywall() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const srcHint = params.get("src") || "";
  const intervalHint = params.get("interval") || "";
  const [pricing, setPricing] = useState(null);
  // Plan type: "individual" (one program) or "joint" (both). Default: individual.
  const [planType, setPlanType] = useState(srcHint === "both" ? "joint" : "individual");
  // Product within plan type. For "individual" we default to the src hint or NGP.
  const [product, setProduct] = useState(
    srcHint === "jidnyasa" ? "jidnyasa"
    : srcHint === "neuroeq" ? "neuroeq"
    : srcHint === "both" ? "both"
    : "neuroeq"
  );
  // Interval default: monthly (per business rule)
  const [interval, setInterval] = useState(intervalHint === "yearly" ? "yearly" : "monthly");
  const [child, setChild] = useState(null);
  const [busy, setBusy] = useState(false);

  // Keep product in sync when planType flips.
  const switchPlanType = (pt) => {
    setPlanType(pt);
    if (pt === "joint") setProduct("both");
    else if (product === "both") setProduct("neuroeq"); // sensible default
  };

  useEffect(() => {
    (async () => {
      try {
        const st = await api.get("/billing/status");
        const active = (st.data.children || []).find((c) => c.is_active_child)
                      || (st.data.children || [])[0];
        if (!active) {
          toast.error("Please add a child profile first.");
          nav("/onboarding/child"); return;
        }
        setChild({ id: active.child_id, name: active.child_name });
        const p = await api.get("/billing/pricing");
        setPricing(p.data.plans);
      } catch (e) { toast.error("Could not load pricing"); }
    })();
  }, [nav]);

  const chosen = pricing?.[product]?.[interval];

  const pay = async () => {
    if (!child || !chosen) return;
    setBusy(true);
    try {
      const orderRes = await api.post("/billing/order",
                                        { child_id: child.id, product, interval });
      const order = orderRes.data;
      const loaded = await loadRazorpay();
      if (!loaded) throw new Error("Could not load Razorpay");
      const options = {
        key: order.key_id,
        amount: order.amount_paise,
        currency: order.currency,
        order_id: order.order_id,
        name: "NeuroEQ",
        description: `${PRODUCTS.find((p) => p.key === product).title} · ${interval === "yearly" ? "Yearly" : "Monthly"}`,
        prefill: { name: order.parent_name, email: order.parent_email },
        notes: { child_name: order.child_name },
        theme: { color: "#FF7A3D" },
        handler: async (r) => {
          try {
            await api.post("/billing/verify", {
              razorpay_order_id: r.razorpay_order_id,
              razorpay_payment_id: r.razorpay_payment_id,
              razorpay_signature: r.razorpay_signature,
            });
            toast.success("Payment successful — access unlocked!");
            // Poll billing/status until entitlement flips (webhook race safety).
            let unlocked = false;
            for (let i = 0; i < 5 && !unlocked; i++) {
              await new Promise((res) => setTimeout(res, 400));
              try {
                const s = await api.get("/billing/status");
                const e = s.data.entitlements || {};
                if ((product === "jidnyasa" && e.jidnyasa)
                    || (product === "neuroeq" && e.neuroeq)
                    || (product === "both" && e.jidnyasa && e.neuroeq)) {
                  unlocked = true;
                }
              } catch {}
            }
            if (product === "jidnyasa") nav("/jidnyasa/dashboard");
            else nav("/home");
          } catch (err) {
            toast.error(err?.response?.data?.detail || "Verification failed. Contact support.");
          }
        },
        modal: { ondismiss: () => setBusy(false) },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start payment");
      setBusy(false);
    }
  };

  if (!pricing || !child) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="paywall-page">
      <div className="max-w-3xl mx-auto px-6 sm:px-8 pt-10">
        <div className="text-xs uppercase tracking-widest text-[hsl(var(--neu-orange))] font-medium">
          Choose your plan
        </div>
        <h1 className="font-display text-4xl sm:text-5xl leading-tight mt-2">
          Two programs. Pick one, or both.
        </h1>
        <p className="text-muted-foreground mt-3 max-w-xl">
          Subscribe to a single program (Individual) or bundle both (Joint). Cancel anytime.
        </p>

        {/* Individual | Joint — plan type selector (large, prominent) */}
        <div className="mt-8 rounded-2xl bg-white/70 border border-black/10 p-1.5 inline-flex w-full sm:w-auto"
              data-testid="paywall-plantype-toggle">
          {[
            { k: "individual", label: "Individual", sub: "One program" },
            { k: "joint",      label: "Joint",      sub: "Both programs · Best value" },
          ].map((o) => {
            const active = planType === o.k;
            return (
              <button key={o.k} onClick={() => switchPlanType(o.k)}
                       className={`flex-1 sm:min-w-[220px] px-6 py-3 rounded-xl text-left transition-all ${
                         active
                           ? "bg-[hsl(var(--neu-ink))] text-white shadow-lg"
                           : "text-[hsl(var(--neu-ink))] hover:bg-white"
                       }`}
                       data-testid={`paywall-plantype-${o.k}`}>
                <div className="font-display text-lg leading-none">{o.label}</div>
                <div className={`text-[11px] mt-1 ${active ? "text-white/70" : "text-muted-foreground"}`}>
                  {o.sub}
                </div>
              </button>
            );
          })}
        </div>

        {/* Billing frequency toggle — Monthly first (default) */}
        <div className="mt-5 inline-flex items-center gap-1 p-1 rounded-full bg-white/70 border border-black/5"
              data-testid="paywall-interval-toggle">
          {["monthly", "yearly"].map((k) => (
            <button key={k} onClick={() => setInterval(k)}
                     className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                       interval === k ? "bg-[hsl(var(--neu-ink))] text-white shadow" : "text-muted-foreground"
                     }`}
                     data-testid={`paywall-interval-${k}`}>
              {k === "monthly" ? "Monthly" : "Yearly · save 20%"}
            </button>
          ))}
        </div>

        {/* Product cards — filtered by planType */}
        <div className={`mt-6 grid grid-cols-1 gap-4 ${
          planType === "individual" ? "md:grid-cols-2" : "md:grid-cols-1 max-w-xl"
        }`}>
          {PRODUCTS
            .filter((p) => planType === "joint" ? p.key === "both" : p.key !== "both")
            .map((p) => {
            const price = pricing[p.key][interval];
            const isChosen = product === p.key;
            const Icon = p.icon;
            const jidnyasa = p.isJidnyasa;
            const accent = jidnyasa ? "hsl(var(--neu-green))" : "hsl(var(--neu-orange))";
            return (
              <button key={p.key} onClick={() => setProduct(p.key)}
                       className={`text-left rounded-3xl p-6 transition-all relative ${
                         isChosen
                           ? "bg-[hsl(var(--neu-ink))] text-white shadow-2xl"
                           : "card-soft hover:-translate-y-0.5"
                       } ${isChosen && jidnyasa ? "ring-2 ring-[hsl(var(--neu-green))]"
                          : isChosen ? "ring-2 ring-[hsl(var(--neu-orange))]" : ""}`}
                       data-testid={`paywall-product-${p.key}`}>
                {p.shortTitle && (
                  <div className={`absolute -top-3 left-6 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full font-medium text-white ${
                    jidnyasa ? "bg-[hsl(var(--neu-green))]" : "bg-[hsl(var(--neu-orange))]"
                  }`}>
                    {p.shortTitle}
                  </div>
                )}
                <Icon className="w-6 h-6" style={{ color: accent }}/>
                <div className={`text-xs uppercase tracking-widest mt-3 ${isChosen ? "text-white/70" : "text-muted-foreground"}`}>
                  {p.subtitle}
                </div>
                <div className="font-display text-2xl mt-1">{p.title}</div>
                <p className={`text-xs mt-1 ${isChosen ? "text-white/75" : "text-muted-foreground"}`}>
                  {p.hint}
                </p>
                <div className="font-display text-3xl mt-4 flex items-baseline gap-2">
                  <span>₹{price.final_inr.toLocaleString("en-IN")}</span>
                  <span className={`text-sm font-normal ${isChosen ? "text-white/70" : "text-muted-foreground"}`}>
                    / {interval === "yearly" ? "year" : "month"}
                  </span>
                </div>
                {interval === "yearly" && (
                  <div className={`text-xs mt-1 ${isChosen ? "text-white/75" : "text-muted-foreground"}`}>
                    That&apos;s ~₹{price.per_month_inr}/month
                  </div>
                )}
                <ul className={`mt-4 space-y-1.5 text-sm ${isChosen ? "text-white/85" : "text-[hsl(var(--neu-ink))]"}`}>
                  {p.bullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: accent }}/>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </button>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-sm text-muted-foreground">
            You&apos;ll pay <b className="text-[hsl(var(--neu-ink))]">₹{chosen?.final_inr?.toLocaleString("en-IN")}</b>{" "}
            for {interval === "yearly" ? "12 months" : "30 days"} of access to{" "}
            <b className="text-[hsl(var(--neu-ink))]">{PRODUCTS.find((p) => p.key === product).title}</b>.
          </div>
          <button onClick={pay} disabled={busy || !chosen}
                   className={product === "jidnyasa" ? "btn-jidnyasa text-lg min-w-[220px]"
                                                       : "btn-primary text-lg min-w-[220px]"}
                   data-testid="paywall-pay-btn">
            {busy
              ? <Loader2 className="w-4 h-4 animate-spin"/>
              : <>Pay ₹{chosen?.final_inr?.toLocaleString("en-IN")} <ArrowRight className="w-4 h-4"/></>}
          </button>
        </div>
        <p className="text-xs text-muted-foreground mt-4">
          Secure payments via Razorpay. UPI, cards, netbanking supported. Manual renewal — no auto-charge.
        </p>
      </div>
    </div>
  );
}

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}
